import { PutUtilityContract, WaterContract } from "@/api/facilities";
import { RootInputNumber } from "@/components/BaseComponents/Inputs";
import ImpactPill from "@/components/Impact/ImpactPill";
import RootModal from "@/components/RootModal/RootModal";
import useAnalytics from "@/hooks/useAnalytics";
import { useGetCoolingOptions, useInsertContract, useUpdateContract } from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import { multiplyImpactWithFactor } from "@/pages/facilities/detail/Utilities/utils";
import usePublishedId from "@/pages/facilities/hooks/usePublishedId";
import { Form, message, Select, Space } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import dayjs from "dayjs";

export default function CoolingModal({
  facilityId,
  open,
  closeModal,
  editContract: contract,
  minDate,
  maxDate,
}: {
  facilityId: number;
  open: boolean;
  closeModal: () => void;
  editContract?: WaterContract;
  minDate: dayjs.Dayjs;
  maxDate: dayjs.Dayjs;
}) {
  const publishedId = usePublishedId();
  const [form] = useForm<{ amount: number; refrigerant: string }>();
  const { data, isFetching } = useGetCoolingOptions(facilityId, open);
  const updateContract = useUpdateContract();
  const insertContract = useInsertContract();
  const analytics = useAnalytics({ enabled: !publishedId });
  const translation = useTranslation();

  const amount = useWatch("amount", form);
  const urn = useWatch("refrigerant", form);

  const selectedRefrigerant = data?.find((x) => x.urn === urn);

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!selectedRefrigerant) {
          message.error(
            "Cooling contract not added. Please enter all required fields and try again."
          );
          return;
        }

        const contractToPutPost: PutUtilityContract = {
          facility_id: facilityId,
          start_date: minDate.format("YYYY-MM-01"),
          end_date: maxDate.format("YYYY-MM-31"),
          type: "cooling",
          contract_type: "actual",
          sources: [
            {
              urn: selectedRefrigerant.urn,
              unit: selectedRefrigerant.unit,
              capital_owned: false,
              amount: values.amount,
              percentage: 1,
            },
          ],
        };

        if (contract) {
          updateContract
            .mutateAsync({
              contractId: contract.id,
              contract: { id: contract.id, ...contractToPutPost },
            })
            .then((result) => {
              if (result.status === 200) {
                message.success("Cooling contract updated.");
                analytics.track("Contract Updated");

                closeModal();
              }
            })
            .catch((error) => message.error(translation.errors.general));
        } else {
          insertContract
            .mutateAsync({
              contract: contractToPutPost,
            })
            .then((result) => {
              if (result.status === 200) {
                analytics.track("Contract Added");
                message.success("Cooling contract added.");
                closeModal();
              }
            })
            .catch((error) => message.error(translation.errors.general));
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <RootModal
        open={open}
        width={750}
        onCancel={() => closeModal()}
        okText={"Save"}
        keyboard={false}
        zIndex={998}
        title={"Add cooling"}
        onOk={onSubmit}
        okButtonProps={{ loading: insertContract.isLoading || updateContract.isLoading }}
      >
        <p>
          For cooling we calculate the impact based on the refrigerant that is annually used by your
          cooling systems at this facility. Read more about cooling.
        </p>
        <Form
          layout="vertical"
          form={form}
          name="cooling_modal"
          initialValues={{
            refrigerant: contract?.sources[0].urn || "R143a",
            amount: contract?.sources[0].amount,
          }}
        >
          <Form.Item
            label="Refrigerant"
            name="refrigerant"
            rules={[
              {
                message: "Refrigerant is required.",
              },
            ]}
          >
            <Select options={data?.map((x) => ({ label: x.label, value: x.urn }))} />
          </Form.Item>
          <Form.Item
            label="Amount"
            required={true}
          >
            <Space>
              <Form.Item
                name="amount"
                noStyle
                rules={[
                  {
                    required: true,
                    message: "Amount is required.",
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <RootInputNumber
                  showSkeleton={isFetching}
                  width={"250px"}
                  addonAfter={selectedRefrigerant?.unit}
                />
              </Form.Item>
              <ImpactPill
                impact={multiplyImpactWithFactor(selectedRefrigerant?.impact, amount)}
                loading={isFetching}
              />
            </Space>
          </Form.Item>
        </Form>
      </RootModal>
    </>
  );
}
